import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import PaymentPlanItem from './PaymentPlanItem';
import EditPaymentPlanDialog from '../dialogs/EditPaymentPlanDialog';
import { Schema } from '../../../../../amplify/data/resource';
import theme from '../../../../theme';
import ReactLoading from 'react-loading';

// @ts-ignore
type PaymentPlan = Schema['PaymentPlan']['type'];

interface PaymentPlanTableProps {
  paymentPlans: PaymentPlan[];
  loading: boolean;
  onPaymentPlanUpdated: () => void;
}

const PaymentPlanTable: React.FC<PaymentPlanTableProps> = ({ paymentPlans, loading, onPaymentPlanUpdated }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<PaymentPlan | null>(null);

  const sortedPaymentPlans = [...paymentPlans].sort((a, b) => {
    // Сортируем по названию плана в алфавитном порядке
    return (a.title || '').localeCompare(b.title || '');
  });

  const handleEditClick = (paymentPlan: PaymentPlan) => {
    setSelectedPaymentPlan(paymentPlan);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedPaymentPlan(null);
  };

  const handleEditSubmit = () => {
    onPaymentPlanUpdated();
    handleEditClose();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
<ReactLoading
            type={'bubbles'}
            color={theme.palette.green.main}
            height={250}
            width={250}
          />
      </Box>
    );
  }

  return (
    <Box sx={{ direction: 'rtl' }}>
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflowY: 'auto',
        }}
      >
        <Table
          stickyHeader
          sx={{
            backgroundColor: 'transparent',
            borderCollapse: 'separate',
            borderSpacing: '0 10px',
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#E7ECF1' }}>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  כותרת
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  מודל חיוב
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  מחיר חיוב
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  מחיר קבוע
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  הנחה
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  תאריך סיום הנחה
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  Services
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  Status
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  padding: '10px 20px',
                  borderBottom: 'none',
                  textAlign: 'right',
                  backgroundColor: '#E7ECF1',
                  borderTopLeftRadius: '10px',
                  borderBottomLeftRadius: '10px',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight="700"
                  fontFamily={'AlmoniDL'}
                >
                  פעולות
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPaymentPlans.map((paymentPlan) => (
              <PaymentPlanItem 
                key={paymentPlan.id} 
                paymentPlan={paymentPlan} 
                onEditClick={handleEditClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedPaymentPlans.length === 0 && !loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={4}
          textAlign="center"
        >
          <Typography
            color="textSecondary"
            fontFamily="AlmoniDL"
            fontSize="16px"
          >
            אין תוכניות תשלום להציג
          </Typography>
        </Box>
      )}

      <EditPaymentPlanDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        paymentPlan={selectedPaymentPlan}
      />
    </Box>
  );
};

export default PaymentPlanTable; 